import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getForumThemes,
  getLatestPosts,
  searchForumPosts,
  getForumDiscussions,
} from "../../services/moodle/forum";
import { CategoryCard } from "../../components/Foro/CategoryCard";
import { LatestPostsSidebar } from "../../components/Foro/LatestPostsSidebar";
import { SearchResults } from "../../components/Foro/SearchResults";
import { RecentPostsFeed } from "../../components/Foro/RecentPostsFeed";
import {
  CategoryCardSkeleton,
  LatestPostsSidebarSkeleton,
} from "../../components/Foro/ForumSkeleton";
import { Search, SlidersHorizontal, Clock, ArrowLeft } from "lucide-react";
import ForumIcon from "@mui/icons-material/Forum";

const ForoPage = () => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showRecent, setShowRecent] = useState(false);

  const {
    data: themes = [],
    isLoading: loading,
    error: themesError,
  } = useQuery({
    queryKey: ["forum", "themes"],
    queryFn: async () => {
      const data = await getForumThemes({ token: auth.user.token });
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(auth.user?.token),
    staleTime: 5 * 60_000,
  });

  // Background prefetch for top categories so first click is instantaneous
  useEffect(() => {
    if (!auth.user?.token || !themes?.length) return;
    const allCategories = themes.flatMap((t) => t.categories || []).slice(0, 4);
    allCategories.forEach((cat) => {
      queryClient.prefetchQuery({
        queryKey: ["forum", "discussions", String(cat.id)],
        queryFn: async () => {
          const data = await getForumDiscussions({
            token: auth.user.token,
            categoryId: cat.id,
          });
          return Array.isArray(data) ? data : [];
        },
        staleTime: 60_000,
      });
    });
  }, [auth.user?.token, themes, queryClient]);

  const {
    data: latestPosts = [],
    isLoading: latestLoading,
  } = useQuery({
    queryKey: ["forum", "latestPosts"],
    queryFn: async () => {
      const data = await getLatestPosts({ token: auth.user.token, limit: 20 });
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(auth.user?.token),
    staleTime: 60_000,
  });

  const error = themesError ? (themesError.message || "Error al cargar categorías") : null;

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        if (!auth.user?.token) return;
        const data = await searchForumPosts({
          token: auth.user.token,
          query: searchQuery.trim(),
          limit: 10,
        });
        if (Array.isArray(data)) {
          setSearchResults(data);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error("Search error:", err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, auth.user]);

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div style={{ minHeight: "calc(100vh - 106px)", paddingBottom: "48px" }}>
        <div className="page-content-shell">
          <style>{`
            .forum-content-layout {
              display: grid;
              grid-template-columns: minmax(0, 1fr);
              gap: 32px;
              align-items: start;
              font-family: 'Montserrat', sans-serif;
            }
            .forum-content-layout > * {
              min-width: 0;
            }
            @media (min-width: 1024px) {
              .forum-content-layout {
                grid-template-columns: minmax(0, 1fr) 380px;
              }
            }
            .gr-header {
              display: flex;
              align-items: center;
              gap: 12px;
              margin-bottom: 28px;
              border-bottom: 1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"};
              padding-bottom: 12px;
              font-family: 'Montserrat', sans-serif;
            }
            .gr-header-icon {
              font-size: 26px;
              line-height: 1;
            }
            .gr-header h2 {
              margin: 0;
              font-size: clamp(26px, 2.5vw, 36px);
              font-weight: 400;
              line-height: 1.2;
              color: ${isDark ? "#e2e1f4" : "inherit"};
            }
            .gr-header h2 strong {
              font-weight: 800;
              border-bottom: 3px solid #ecb236;
              padding-bottom: 11px;
              display: inline-block;
            }
            .gr-sep {
              color: ${isDark ? "#9a9a9a" : "#6b7280"};
              font-weight: 300;
              margin: 0 6px;
            }
            .gr-header-subtitle {
              font-size: 16px;
            }
            @media (max-width: 767px) {
              .gr-header h2 {
                font-size: 19px !important;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 2px;
                line-height: 1.25;
              }
              .gr-header h2 strong {
                border-bottom: 2px solid #ecb236;
                padding-bottom: 3px;
                margin-bottom: 2px;
              }
              .gr-sep {
                display: none !important;
              }
              .gr-header-subtitle {
                font-size: 15px !important;
                color: ${isDark ? "#9a9a9a" : "#6b7280"} !important;
                font-weight: 500 !important;
              }
            }
            .forum-search-bar {
              display: flex;
              gap: 16px;
              align-items: center;
              margin-bottom: 32px;
              font-family: 'Montserrat', sans-serif;
            }
            .forum-search-input-box {
              flex: 1;
              display: flex;
              align-items: center;
              gap: 14px;
              background-color: ${isDark ? "rgba(17, 18, 31, 0.85)" : theme.colors.cardBackground || "#ffffff"};
              border: 1px solid #ECB236;
              border-radius: 12px;
              padding: 16px 22px;
              transition: border-color 0.2s, box-shadow 0.2s;
            }
            .forum-search-input {
              flex: 1;
              background: transparent;
              border: none;
              outline: none;
              color: #ffffff;
              font-size: 16px;
              font-family: 'Montserrat', sans-serif;
              font-weight: 500;
            }
            .forum-search-actions {
              display: flex;
              align-items: center;
              gap: 16px;
              flex-shrink: 0;
            }
            .forum-search-btn-filtros {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              border: 1px solid #ECB236;
              border-radius: 12px;
              padding: 16px 24px;
              background-color: #ECB236;
              color: #11121F;
              font-size: 16px;
              font-weight: 700;
              cursor: pointer;
              transition: all 0.2s ease;
              flex-shrink: 0;
              font-family: 'Montserrat', sans-serif;
            }
            .forum-search-btn-filtros:hover {
              background-color: #f5be47;
            }
            .forum-search-btn-recientes {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              border-radius: 12px;
              padding: 16px 24px;
              font-size: 16px;
              font-weight: 700;
              cursor: pointer;
              transition: all 0.2s ease;
              flex-shrink: 0;
              font-family: 'Montserrat', sans-serif;
            }
            @media (max-width: 767px) {
              .forum-search-bar {
                flex-direction: column;
                gap: 12px;
                margin-bottom: 24px;
              }
              .forum-search-input-box {
                width: 100%;
                padding: 12px 16px;
              }
              .forum-search-input {
                font-size: 15px !important;
              }
              .forum-search-actions {
                display: flex;
                width: 100%;
                gap: 10px;
              }
              .forum-search-btn-filtros,
              .forum-search-btn-recientes {
                flex: 1;
                padding: 11px 16px;
                font-size: 15px !important;
              }
            }
          `}</style>
          {loading ? (
            <>
              {/* Search bar skeleton */}
              <div className="flex flex-col tablet:flex-row gap-3 tablet:gap-4 mb-8">
                <Skeleton
                  height={52}
                  borderRadius={12}
                  baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                  highlightColor={isDark ? "#252642" : "#f1f5f9"}
                  style={{ flex: 1 }}
                />
                <div className="flex gap-2.5 tablet:gap-4">
                  <Skeleton
                    width={110}
                    height={52}
                    borderRadius={12}
                    baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                    highlightColor={isDark ? "#252642" : "#f1f5f9"}
                  />
                  <Skeleton
                    width={130}
                    height={52}
                    borderRadius={12}
                    baseColor={isDark ? "#1e1f3a" : "#e2e8f0"}
                    highlightColor={isDark ? "#252642" : "#f1f5f9"}
                  />
                </div>
              </div>
              <div className="forum-content-layout">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CategoryCardSkeleton count={4} />
                </div>
                <div>
                  <LatestPostsSidebarSkeleton count={4} />
                </div>
              </div>
            </>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "40px", color: "red" }}>
              Error: {error}
            </div>
          ) : (
            <>
              {/* Search bar & Action Buttons */}
              <div className="forum-search-bar">
                <div className="forum-search-input-box">
                  <Search size={22} className="text-[#ECB236] shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar discusiones, temas o palabras clave..."
                    className="forum-search-input placeholder:text-white/40"
                  />
                </div>

                <div className="forum-search-actions">
                  {/* Filtros button */}
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById("tema-normas");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="forum-search-btn-filtros"
                  >
                    <SlidersHorizontal size={18} color="#11121f" />
                    Filtros
                  </button>

                  {/* Recientes / Volver a categorías button */}
                  <button
                    type="button"
                    aria-pressed={showRecent}
                    onClick={() => {
                      setShowRecent((prev) => !prev);
                      if (searchQuery) setSearchQuery("");
                    }}
                    className="forum-search-btn-recientes"
                    style={{
                      border: showRecent
                        ? "1px solid #ECB236"
                        : "1px solid rgba(236, 178, 54, 0.45)",
                      backgroundColor: showRecent
                        ? "#ECB236"
                        : isDark
                        ? "rgba(17, 18, 31, 0.85)"
                        : theme.colors.cardBackground || "#ffffff",
                      color: showRecent ? "#11121f" : "#ECB236",
                    }}
                  >
                    {showRecent ? (
                      <ArrowLeft size={18} color="#11121f" />
                    ) : (
                      <Clock size={18} color="#ECB236" />
                    )}
                    {showRecent ? "Volver a categorías" : "Recientes"}
                  </button>
                </div>
              </div>

              {!isSearching && (
                <div className="gr-header">
                  <span
                    className="gr-header-icon text-md-primary-container flex items-center justify-center shrink-0"
                    role="img"
                    aria-label={showRecent ? "recientes" : "categorías"}
                  >
                    {showRecent ? (
                      <Clock size={32} color="#ECB236" />
                    ) : (
                      <ForumIcon sx={{ fontSize: 32, color: "#ECB236" }} />
                    )}
                  </span>
                  <h2>
                    <strong>{showRecent ? "Recientes" : "Categorías"}</strong>{" "}
                    <span className="gr-sep">|</span>{" "}
                    <span className="gr-header-subtitle">
                      {showRecent ? "Últimas publicaciones" : "Temas de discusión"}
                    </span>
                  </h2>
                </div>
              )}

              <div className="forum-content-layout">
                <div>
                  {isSearching ? (
                    <SearchResults
                      results={searchResults}
                      query={searchQuery}
                      loading={searchLoading}
                    />
                  ) : showRecent ? (
                    <RecentPostsFeed
                      posts={latestPosts}
                      loading={latestLoading}
                      onBackToCategories={() => setShowRecent(false)}
                    />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "36px" }}>
                      {themes.map((theme) => (
                        <div
                          key={theme.id}
                          id={
                            theme.name.toLowerCase().includes("norma") ||
                            theme.categories?.some((c) => c.name.toLowerCase().includes("norma"))
                              ? "tema-normas"
                              : undefined
                          }
                          style={{ display: "flex", flexDirection: "column", gap: "18px" }}
                        >
                          {/* Theme Header */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderBottom: "1px solid rgba(255,255,255,0.12)",
                              paddingBottom: "12px",
                              marginBottom: "4px",
                            }}
                          >
                            <h2
                              style={{
                                color: "#ECB236",
                                margin: 0,
                                fontSize: "22px",
                                fontWeight: 700,
                              }}
                            >
                              {theme.name}
                            </h2>
                          </div>

                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: "16px",
                            }}
                          >
                            {theme.categories.length === 0 ? (
                              <p
                                style={{
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontSize: "var(--font-size-min, 15px)",
                                  margin: 0,
                                  fontStyle: "italic",
                                }}
                              >
                                No hay categorías en esta temática.
                              </p>
                            ) : (
                              theme.categories.map((cat) => (
                                <CategoryCard key={cat.id} category={cat} />
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <aside aria-label="Publicaciones recientes">
                  <LatestPostsSidebar
                    posts={latestPosts.slice(0, 5)}
                    loading={latestLoading}
                    themes={themes}
                  />
                </aside>
              </div>
            </>
          )}
        </div>
      </div>
  );
};

export default ForoPage;
